<script lang="ts">
import type { SwitchRootEmits, SwitchRootProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { SwitchRoot, SwitchThumb, useForwardPropsEmits } from 'reka-ui'

export type SwitchSize = 'sm' | 'md' | 'lg'

export type SwitchProps = SwitchRootProps & {
  size?: SwitchSize
  class?: HTMLAttributes['class']
}
</script>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import { cva } from 'class-variance-authority'
import { cn } from '#ui-kit/lib/utils'

const props = defineProps<SwitchProps>()
const emits = defineEmits<SwitchRootEmits>()

const switchVariants = cva(
  'peer inline-flex shrink-0 cursor-pointer items-center rounded-full p-0.5 ring-1 ring-neutral-700 transition-colors duration-200 focus-visible:outline-hidden focus-visible:ring-primary-300 focus-visible:ring-offset-2 disabled:cursor-not-allowed data-[state=checked]:bg-primary-300 data-[state=unchecked]:bg-neutral-200 data-disabled:data-[state=checked]:bg-neutral-400',
  {
    variants: {
      size: {
        sm: 'h-4 w-7',
        md: 'h-5 w-9',
        lg: 'h-6 w-11',
      },
    },
    defaultVariants: { size: 'md' },
  },
)

const thumbVariants = cva(
  'pointer-events-none block rounded-full shadow-lg transition-[transform,background-color] duration-200 data-[state=checked]:bg-background data-[state=unchecked]:bg-primary-900 data-[state=unchecked]:translate-x-0 data-disabled:data-[state=checked]:bg-neutral-600',
  {
    variants: {
      size: {
        sm: 'h-3 w-3 data-[state=checked]:translate-x-3',
        md: 'h-4 w-4 data-[state=checked]:translate-x-4',
        lg: 'h-5 w-5 data-[state=checked]:translate-x-5',
      },
    },
    defaultVariants: { size: 'md' },
  },
)

const delegated = reactiveOmit(props, 'class', 'size')
const forwarded = useForwardPropsEmits(delegated, emits)
</script>

<template>
  <SwitchRoot v-bind="forwarded" :class="cn(switchVariants({ size: props.size }), props.class)">
    <SwitchThumb :class="thumbVariants({ size: props.size })" />
  </SwitchRoot>
</template>
